import { useCustom } from '@refinedev/core'
import {
  Button,
  Card,
  Col,
  Empty,
  Row,
  Spin,
  Statistic,
  Table,
  Typography
} from 'antd'
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  KeyOutlined,
  ReloadOutlined,
  UnorderedListOutlined
} from '@ant-design/icons'
import { PageHeader, PageShell } from '@/components/page-shell'

const { Text } = Typography

/**
 * 跨项目统计看板（F4）
 *
 * 数据源：GET /api/admin/stats → PlatformStats
 *
 * 展示：
 *   - 顶部 4 张核心指标卡片（总项目/总序列号/活跃/已吊销）
 *   - 中部左：辅助指标（未使用/设备总数/近7天/近30天激活）
 *   - 中部右：项目分布表（byProject）
 */

/** 后端 /api/admin/stats 实际返回的结构 */
interface PlatformStats {
  totalProjects: number
  activeProjects: number
  suspendedProjects: number
  archivedProjects: number
  totalLicenses: number
  activeLicenses: number
  unusedLicenses: number
  revokedLicenses: number
  totalDevices: number
  recentActivations7d: number
  recentActivations30d: number
  byProject: Array<{
    projectCode: string
    name: string
    status: string
    totalLicenses: number
    activeLicenses: number
    revokedLicenses: number
    totalDevices: number
  }>
}

/** 项目状态徽章颜色映射 */
const STATUS_COLOR: Record<string, string> = {
  active: '#10B981',
  suspended: '#F59E0B',
  archived: '#64748B'
}

const STATUS_LABEL: Record<string, string> = {
  active: '运营中',
  suspended: '已暂停',
  archived: '已归档'
}

export default function DashboardPage() {
  const { data, isLoading, isError, refetch } = useCustom<PlatformStats>({
    url: '/api/admin/stats',
    method: 'get'
  })

  const stats = data?.data

  if (isLoading) {
    return (
      <div className="app-loading-state" style={{ textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    )
  }

  if (isError || !stats) {
    return (
      <div className="app-loading-state" style={{ textAlign: 'center' }}>
        <Empty description="加载失败" />
        <Button icon={<ReloadOutlined />} onClick={() => refetch()} style={{ marginTop: 16 }}>
          重试
        </Button>
      </div>
    )
  }

  return (
    <PageShell className="page-enter" width="wide">
      <PageHeader
        title="跨项目统计看板"
        subtitle="统一观察项目、序列号、设备与激活走势，便于快速判断平台运行状态。"
      />

      {/* 顶部 4 张核心指标卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }} className="stagger-list">
        <Col xs={12} sm={12} md={6}>
          <Card className="app-kpi-card app-surface-card" style={{ height: '100%' }}>
            <Statistic
              title="总项目"
              value={stats.totalProjects}
              prefix={<AppstoreOutlined style={{ color: '#10B981' }} />}
              valueStyle={{ fontWeight: 600 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card className="app-kpi-card app-surface-card" style={{ height: '100%' }}>
            <Statistic
              title="总序列号"
              value={stats.totalLicenses}
              prefix={<KeyOutlined style={{ color: '#0EA5E9' }} />}
              valueStyle={{ fontWeight: 600 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card className="app-kpi-card app-surface-card" style={{ height: '100%' }}>
            <Statistic
              title="活跃"
              value={stats.activeLicenses}
              valueStyle={{ color: '#10B981', fontWeight: 600 }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card className="app-kpi-card app-surface-card" style={{ height: '100%' }}>
            <Statistic
              title="已吊销"
              value={stats.revokedLicenses}
              valueStyle={{ color: '#F43F5E', fontWeight: 600 }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* 中部左：辅助指标 */}
        <Col xs={24} lg={10}>
          <Card title="辅助指标" size="small" className="app-surface-card">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="未使用序列号"
                  value={stats.unusedLicenses}
                  valueStyle={{ color: '#F59E0B' }}
                />
              </Col>
              <Col span={12}>
                <Statistic title="设备总数" value={stats.totalDevices} />
              </Col>
              <Col span={12}>
                <Statistic
                  title="近 7 天激活"
                  value={stats.recentActivations7d}
                  valueStyle={{ color: '#10B981' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="近 30 天激活"
                  value={stats.recentActivations30d}
                  valueStyle={{ color: '#059669' }}
                />
              </Col>
            </Row>
            <div
              style={{
                marginTop: 16,
                paddingTop: 12,
                borderTop: '1px solid var(--ant-color-border-secondary, #F1F5F9)',
                fontSize: 12,
                color: 'var(--ant-color-text-tertiary, #64748B)',
                lineHeight: 1.6
              }}
            >
              <UnorderedListOutlined style={{ marginRight: 6 }} />
              数据来源：全平台聚合统计，每分钟刷新一次
            </div>
          </Card>
        </Col>

        {/* 中部右：项目分布表 */}
        <Col xs={24} lg={14}>
          <Card title="项目分布" size="small" className="app-surface-card">
            <Table
              rowKey="projectCode"
              dataSource={stats.byProject ?? []}
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
              locale={{
                emptyText: (
                  <Empty description="暂无项目" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )
              }}
              columns={[
                {
                  title: '项目',
                  dataIndex: 'name',
                  render: (name: string, record) => (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Text strong style={{ fontSize: 13 }}>
                        {name}
                      </Text>
                      <code
                        style={{
                          fontFamily: 'JetBrains Mono, monospace',
                          fontSize: 11,
                          color: '#10B981'
                        }}
                      >
                        {record.projectCode}
                      </code>
                    </div>
                  )
                },
                {
                  title: '状态',
                  dataIndex: 'status',
                  width: 90,
                  render: (status: string) => (
                    <span
                      className="status-badge"
                      style={{
                        color: STATUS_COLOR[status] ?? '#64748B',
                        background: `${STATUS_COLOR[status] ?? '#64748B'}1A`,
                        border: `1px solid ${STATUS_COLOR[status] ?? '#64748B'}33`
                      }}
                    >
                      {STATUS_LABEL[status] ?? status}
                    </span>
                  )
                },
                {
                  title: '总序列号',
                  dataIndex: 'totalLicenses',
                  width: 90,
                  align: 'right' as const,
                  render: (v: number) => (
                    <span className="tabular-nums">{v}</span>
                  )
                },
                {
                  title: '活跃',
                  dataIndex: 'activeLicenses',
                  width: 70,
                  align: 'right' as const,
                  render: (v: number) => (
                    <span className="tabular-nums" style={{ color: '#10B981' }}>
                      {v}
                    </span>
                  )
                },
                {
                  title: '已吊销',
                  dataIndex: 'revokedLicenses',
                  width: 70,
                  align: 'right' as const,
                  render: (v: number) => (
                    <span className="tabular-nums" style={{ color: '#F43F5E' }}>
                      {v}
                    </span>
                  )
                },
                {
                  title: '设备',
                  dataIndex: 'totalDevices',
                  width: 70,
                  align: 'right' as const,
                  render: (v: number) => <span className="tabular-nums">{v}</span>
                }
              ]}
            />
          </Card>
        </Col>
      </Row>

      {/* 底部：项目状态汇总（紧凑展示） */}
      <Card size="small" className="app-surface-card">
        <div
          style={{
            display: 'flex',
            gap: 32,
            flexWrap: 'wrap',
            alignItems: 'center',
            fontSize: 13
          }}
        >
          <span style={{ color: 'var(--ant-color-text-tertiary, #64748B)' }}>项目状态汇总：</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#10B981'
              }}
            />
            运营中 {stats.activeProjects}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#F59E0B'
              }}
            />
            已暂停 {stats.suspendedProjects}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#64748B'
              }}
            />
            已归档 {stats.archivedProjects}
          </span>
        </div>
      </Card>
    </PageShell>
  )
}
